'use client'

import React, { useMemo } from 'react'
import { ChevronUp, Gauge, Minus, Pause, Play, Plus, SkipBack, SkipForward, Speech, X } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  MAX_SPEECH_RATE,
  MIN_SPEECH_RATE,
} from '@/app/components/functions/util/voice-speech'
import { shortVoiceNames } from '@/app/components/functions/util/voice-names'

/**
 * The controls for the speech engine, as a bar over the text.
 *
 * These used to be scattered: play sat in the header and the speed was not
 * adjustable at all. The transport and the sound settings now live together here,
 * and the bar stays open while the speech runs.
 *
 * ONE ROW, and that is the constraint everything else here is fitted to. This bar
 * floats over the text the reader is trying to follow, so its height is lines of
 * that text they cannot see. It was two rows while the voice picker had one to
 * itself - a full-width native select for a setting most readers change once, if
 * ever, paying for itself in hidden lines the entire time anyone listened. The
 * client called it large and not pretty, and the height was the larger half of
 * that.
 *
 * So the row holds what is used continuously - previous, play, next, close - and
 * one chip that shows the two things that decide how the reading SOUNDS, speed and
 * voice, and opens a small panel to change them. The chip always says what is set,
 * so nothing is hidden by moving it: "1,0x · Zofia" answers the question a reader
 * would otherwise open the panel to ask.
 *
 * Speed and voice sit together in that panel because they are the same kind of
 * choice. Before, one was a tinted pill with buttons and the other a bordered
 * system dropdown - two visual languages for one idea.
 *
 * The panel does not block the page. Radix's popover is non-modal by default, so
 * speech carries on while it is open and a change is heard straight away; that
 * matters, because choosing a voice or a speed is mostly a matter of listening to
 * it, and a dialog that paused the reading would make that impossible.
 */
export interface SpeechBarProps {
  isPlaying: boolean
  isPaused: boolean
  /** Speech rate in use, between MIN_SPEECH_RATE and MAX_SPEECH_RATE. */
  rate: number
  onRateStep: (direction: 1 | -1) => void
  /** Voices installed for the reading language, already filtered and sorted. */
  voices: SpeechSynthesisVoice[]
  /** The chosen voice's URI, or 'auto' to let scoreVoice decide. */
  voiceURI: string
  onVoiceChange: (voiceURI: string) => void
  onPlayPause: (event: React.MouseEvent<HTMLButtonElement>) => void
  onPrevious: () => void
  onNext: () => void
  onClose: () => void
}

const AUTO_VOICE = 'auto'

const CONTROL_BUTTON_CLASS =
  'flex flex-shrink-0 items-center justify-center rounded-full text-[#1E3144] transition-colors hover:bg-[#1E3144]/10 active:bg-[#1E3144]/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2B0448] disabled:opacity-30 disabled:hover:bg-transparent disabled:active:bg-transparent'

const SECTION_LABEL_CLASS = 'mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[#1E3144]/60'

/** Shown between the two speed buttons, e.g. "1,25x" - a comma, the UI is Polish. */
function formatRate(rate: number): string {
  return `${rate.toFixed(2).replace(/0$/, '').replace(/\.$/, '').replace('.', ',')}x`
}

export default function SpeechBar({
  isPlaying,
  isPaused,
  rate,
  onRateStep,
  voices,
  voiceURI,
  onVoiceChange,
  onPlayPause,
  onPrevious,
  onNext,
  onClose,
}: SpeechBarProps) {
  const voiceLabels = useMemo(() => shortVoiceNames(voices), [voices])

  /*
   * A saved voice that this device no longer has - chosen on another browser, or
   * uninstalled since - is not shown as selected, because the engine will not use
   * it either: selectVoice falls back to the ranking, which is what "Automatyczny"
   * means. Showing the stale name would claim a voice the reader is not hearing.
   */
  const selectedVoice =
    voiceURI !== AUTO_VOICE && voices.some((voice) => voice.voiceURI === voiceURI)
      ? voiceURI
      : AUTO_VOICE
  const currentVoiceLabel =
    selectedVoice === AUTO_VOICE ? 'Automatyczny' : voiceLabels.get(selectedVoice) ?? 'Automatyczny'
  // The chip is a summary, so the automatic voice is "Auto" there; the panel, where
  // the choice is actually made, keeps the full word.
  const chipVoiceLabel = selectedVoice === AUTO_VOICE ? 'Auto' : currentVoiceLabel

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 px-3 pb-3 pt-1"
      role="region"
      aria-label="Sterowanie czytaniem na głos"
    >
      <div className="mx-auto flex max-w-xl items-center gap-1 rounded-2xl border border-[#1E3144]/10 bg-white/95 p-2 shadow-[0_4px_24px_-6px_rgba(30,49,68,0.35)] backdrop-blur">
        <button
          type="button"
          onClick={onPrevious}
          aria-label="Poprzedni fragment"
          title="Poprzedni fragment"
          className={`${CONTROL_BUTTON_CLASS} h-9 w-9 sm:h-10 sm:w-10`}
        >
          <SkipBack className="h-5 w-5" />
        </button>

        <button
          type="button"
          onClick={onPlayPause}
          aria-label={isPlaying ? 'Pauza' : isPaused ? 'Kontynuuj' : 'Czytaj na głos'}
          title={isPlaying ? 'Pauza' : isPaused ? 'Kontynuuj' : 'Czytaj na głos'}
          className={`${CONTROL_BUTTON_CLASS} h-11 w-11 bg-[#2B0448] text-[#FBF8FF] shadow-sm hover:bg-[#2B0448]/90 active:bg-[#2B0448]`}
        >
          {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
        </button>

        <button
          type="button"
          onClick={onNext}
          aria-label="Następny fragment"
          title="Następny fragment"
          className={`${CONTROL_BUTTON_CLASS} h-9 w-9 sm:h-10 sm:w-10`}
        >
          <SkipForward className="h-5 w-5" />
        </button>

        <Popover>
          {/*
            * The chip. It takes whatever width the row has left and truncates the
            * voice name rather than the speed, because the speed is short and the
            * voice is the part a reader can recognise from a few letters.
            *
            * Each value carries its own icon - a gauge for the speed, a speaking head
            * for the voice - and the same two stand beside the headings in the panel,
            * so the chip and the panel it opens say the same thing in the same
            * pictures. The icons also replace the dot that used to separate the two
            * values, which is most of the room they need.
            *
            * The chevron still goes below sm. On a 360px phone the row leaves the chip
            * about 130px, and an earlier version with an extra icon and the chevron
            * left the voice name 15 of them - "Z…", which names nothing. The tinted
            * pill reads as a control without the chevron.
            */}
          <PopoverTrigger
            aria-label={`Ustawienia dźwięku: prędkość ${formatRate(rate)}, głos ${currentVoiceLabel}`}
            className="ml-auto flex h-10 min-w-0 max-w-[14rem] items-center gap-1 rounded-full sm:max-w-[18rem] sm:gap-1.5 bg-[#1E3144]/[0.07] pl-2.5 pr-2.5 sm:pl-3 sm:pr-2 text-sm text-[#1E3144] transition-colors hover:bg-[#1E3144]/[0.12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2B0448] data-[state=open]:bg-[#1E3144]/[0.12]"
          >
            <Gauge className="h-3.5 w-3.5 flex-shrink-0 text-[#1E3144]/60 sm:h-4 sm:w-4" aria-hidden="true" />
            <span className="flex-shrink-0 font-medium tabular-nums">{formatRate(rate)}</span>
            <Speech className="ml-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#1E3144]/60 sm:ml-1.5 sm:h-4 sm:w-4" aria-hidden="true" />
            <span className="min-w-0 truncate">{chipVoiceLabel}</span>
            <ChevronUp className="hidden h-4 w-4 flex-shrink-0 text-[#1E3144]/50 sm:block" aria-hidden="true" />
          </PopoverTrigger>

          {/*
            * Above the bar, never below: below it is the edge of the screen. The
            * collision padding keeps the panel inside a 360px phone with a margin
            * either side.
            */}
          <PopoverContent
            side="top"
            align="end"
            sideOffset={10}
            collisionPadding={12}
            className="w-[min(20rem,calc(100vw-1.5rem))] rounded-2xl border-[#1E3144]/10 bg-white p-4 text-[#1E3144] shadow-[0_8px_32px_-8px_rgba(30,49,68,0.4)]"
          >
            <section aria-labelledby="speech-rate-label">
              <h3 id="speech-rate-label" className={SECTION_LABEL_CLASS}>
                <Gauge className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                Prędkość
              </h3>
              {/*
                * The same pair of buttons as before, a step of 0.1 across 0.5-2.0 -
                * kept on the client's choice over a row of presets. A tinted pill
                * holds the buttons and the value together, which is what tells the
                * reader the number belongs to them.
                */}
              <div className="flex items-center justify-between rounded-full bg-[#1E3144]/[0.07] p-1">
                <button
                  type="button"
                  onClick={() => onRateStep(-1)}
                  disabled={rate <= MIN_SPEECH_RATE}
                  aria-label="Czytaj wolniej"
                  title="Czytaj wolniej"
                  className={`${CONTROL_BUTTON_CLASS} h-10 w-10 bg-white shadow-sm`}
                >
                  <Minus className="h-4 w-4" />
                </button>

                {/*
                  * The speed is announced as it changes, since the buttons
                  * themselves cannot say what they moved it to.
                  */}
                <span
                  aria-live="polite"
                  aria-label={`Prędkość czytania ${formatRate(rate)}`}
                  className="text-base font-semibold tabular-nums"
                >
                  {formatRate(rate)}
                </span>

                <button
                  type="button"
                  onClick={() => onRateStep(1)}
                  disabled={rate >= MAX_SPEECH_RATE}
                  aria-label="Czytaj szybciej"
                  title="Czytaj szybciej"
                  className={`${CONTROL_BUTTON_CLASS} h-10 w-10 bg-white shadow-sm`}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </section>

            <section aria-labelledby="speech-voice-label" className="mt-5">
              <h3 id="speech-voice-label" className={SECTION_LABEL_CLASS}>
                <Speech className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                Głos
              </h3>
              {/*
                * Native radios rather than a styled list: they bring the arrow
                * keys, the checked state and the group semantics a screen reader
                * needs without any of it being reimplemented. Each row is 44px, the
                * smallest target iOS treats as reliably tappable.
                *
                * The row shows the short name and keeps the engine's full name as a
                * tooltip, so nothing is lost for the one reader who wants to know it
                * is the network Zofia and not the local one.
                */}
              <div
                role="radiogroup"
                aria-labelledby="speech-voice-label"
                className="-mx-1 max-h-56 overflow-y-auto"
              >
                {[{ voiceURI: AUTO_VOICE, label: 'Automatyczny', hint: 'zalecany', full: undefined as string | undefined }]
                  .concat(
                    voices.map((voice) => ({
                      voiceURI: voice.voiceURI,
                      label: voiceLabels.get(voice.voiceURI) ?? voice.name,
                      hint: '',
                      full: voice.name,
                    })),
                  )
                  .map((option) => {
                    const checked = selectedVoice === option.voiceURI
                    return (
                      <label
                        key={option.voiceURI}
                        title={option.full}
                        className={`flex min-h-11 cursor-pointer items-center gap-3 rounded-lg px-2 text-sm transition-colors hover:bg-[#1E3144]/[0.06] ${
                          checked ? 'font-semibold' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="speech-voice"
                          value={option.voiceURI}
                          checked={checked}
                          onChange={() => onVoiceChange(option.voiceURI)}
                          className="h-4 w-4 flex-shrink-0 accent-[#2B0448]"
                        />
                        <span className="min-w-0 truncate">{option.label}</span>
                        {option.hint && (
                          <span className="ml-auto flex-shrink-0 text-xs font-normal text-[#1E3144]/50">
                            {option.hint}
                          </span>
                        )}
                      </label>
                    )
                  })}
              </div>
            </section>
          </PopoverContent>
        </Popover>

        {/* Closing is not a playback control, so it sits past a divider. */}
        <span className="mx-0.5 h-6 w-px flex-shrink-0 bg-[#1E3144]/10" aria-hidden="true" />

        <button
          type="button"
          onClick={onClose}
          aria-label="Zamknij sterowanie czytaniem"
          title="Zamknij sterowanie czytaniem"
          className={`${CONTROL_BUTTON_CLASS} h-9 w-9`}
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  )
}
